import { cn, formatCFA, formatDateFR } from '@/lib/utils'
import type { PrintFormat, OwnerInvoiceProfile } from '@/lib/invoice-branding'
import { resolveInvoiceSellerIdentity } from '@/lib/invoice-branding'
import { forwardRef, useState } from 'react'

type BillItem = {
  name: string
  quantity: number
  unitPrice: number
  lineTotal: number
}

type BillCustomer = {
  name: string
}

type PrintableBill = {
  id: string
  createdAt: string
  incurredOn?: string | null
  status: 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'VOID'
  subtotal: number
  tax: number
  total: number
  paid: number
  balance: number
  customer: BillCustomer
  items: BillItem[]
}

type Props = {
  bill: PrintableBill
  ownerProfile?: OwnerInvoiceProfile | null
  customerBalance?: number
  printFormat?: PrintFormat
}

const STATUS_FR: Record<PrintableBill['status'], string> = {
  DRAFT: 'Brouillon',
  ISSUED: 'Émise',
  PARTIALLY_PAID: 'Partiellement payée',
  PAID: 'Payée',
  VOID: 'Annulée',
}

function billStatusFr(status: PrintableBill['status']) {
  return STATUS_FR[status] ?? status
}

export const BillPrintDocument = forwardRef<HTMLDivElement, Props>(
  function BillPrintDocument(
    { bill, ownerProfile, customerBalance = 0, printFormat },
    ref
  ) {
    const seller = resolveInvoiceSellerIdentity(ownerProfile)
    const mode = printFormat ?? seller.printFormat

    if (mode === 'THERMAL_58' || mode === 'THERMAL_80') {
      return (
        <div
          ref={ref}
          id='printable-area'
          className={cn(
            'bg-white text-black mx-auto',
            mode === 'THERMAL_58' ? 'w-[56mm]' : 'w-[78mm]'
          )}
        >
          <ThermalStyles mode={mode} />
          <ThermalReceipt
            bill={bill}
            seller={seller}
            customerBalance={customerBalance}
          />
        </div>
      )
    }

    return (
      <div
        ref={ref}
        id='printable-area'
        className='bg-white text-black rounded-lg border p-6 max-w-4xl mx-auto'
      >
        <A4Styles />
        <A4Invoice
          bill={bill}
          seller={seller}
          customerBalance={customerBalance}
        />
      </div>
    )
  }
)

function SellerLogo({
  logoUrl,
  alt,
  size = 64,
}: {
  logoUrl?: string
  alt: string
  size?: number
}) {
  const [error, setError] = useState(false)

  if (!logoUrl || error) return null

  return (
    <img
      src={logoUrl}
      alt={alt}
      style={{ width: size, height: size, objectFit: 'contain' }}
      className='shrink-0 rounded'
      crossOrigin='anonymous'
      onError={() => setError(true)}
    />
  )
}

function A4Invoice({
  bill,
  seller,
  customerBalance,
}: {
  bill: PrintableBill
  seller: ReturnType<typeof resolveInvoiceSellerIdentity>
  customerBalance: number
}) {
  return (
    <div className='space-y-6'>
      <div className='flex items-start justify-between gap-6 border-b pb-4'>
        <div className='flex items-start gap-4'>
          <SellerLogo
            logoUrl={seller.logoUrl}
            alt={seller.displayName}
            size={72}
          />
          <div>
            <div className='text-2xl font-bold tracking-tight'>
              {seller.displayName}
            </div>

            {seller.phone && <div className='text-sm mt-1'>{seller.phone}</div>}

            {seller.address && (
              <div className='text-sm text-slate-700 mt-1'>
                {seller.address}
              </div>
            )}

            <div className='text-xs text-slate-500 mt-2'>
              {seller.showOwnerIdentity
                ? seller.poweredBy
                : 'Facture générée avec MOGESAPP'}
            </div>
          </div>
        </div>

        <div className='text-right'>
          <div className='text-2xl font-bold'>FACTURE</div>
          <div className='text-xs text-slate-500 mt-1'>
            {seller.displayName}
          </div>

          <div className='text-sm mt-2'>
            <div>N° : {bill.id.slice(0, 8)}</div>
            <div>Date : {formatDateFR(bill.incurredOn || bill.createdAt)}</div>
            <div>
              Statut : <b>{billStatusFr(bill.status)}</b>
            </div>
          </div>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-6'>
        <div>
          <div className='text-xs uppercase tracking-wide text-slate-500 mb-1'>
            Client
          </div>
          <div className='font-medium'>{bill.customer.name}</div>
        </div>

        <div className='text-right' />
      </div>

      <table className='w-full text-sm border-collapse'>
        <thead>
          <tr className='border-b bg-slate-50'>
            <th className='text-left py-2 px-2'>Article</th>
            <th className='text-right py-2 px-2'>PU</th>
            <th className='text-right py-2 px-2'>Qté</th>
            <th className='text-right py-2 px-2'>Total</th>
          </tr>
        </thead>
        <tbody>
          {bill.items.map((li) => (
            <tr
              key={`${li.name}-${li.quantity}-${li.unitPrice}`}
              className='border-b last:border-b-0'
            >
              <td className='py-2 px-2'>{li.name}</td>
              <td className='text-right py-2 px-2 tabular-nums whitespace-nowrap'>
                {formatCFA(li.unitPrice)}
              </td>
              <td className='text-right py-2 px-2 tabular-nums'>
                {li.quantity}
              </td>
              <td className='text-right py-2 px-2 tabular-nums whitespace-nowrap'>
                {formatCFA(li.lineTotal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className='ml-auto max-w-sm space-y-2 text-sm'>
        <Row label='Sous-total' value={formatCFA(bill.subtotal)} />
        <Row label='Taxes' value={formatCFA(bill.tax)} />
        <Row label='Total' value={formatCFA(bill.total)} strong />
        <Row label='Payé' value={formatCFA(bill.paid)} />
        <Row label='Restant' value={formatCFA(bill.balance)} strong />
        <hr className='my-2' />
        <Row
          label='Solde global client'
          value={formatCFA(Math.abs(customerBalance))}
        />
      </div>
    </div>
  )
}

function ThermalReceipt({
  bill,
  seller,
  customerBalance,
}: {
  bill: PrintableBill
  seller: ReturnType<typeof resolveInvoiceSellerIdentity>
  customerBalance: number
}) {
  return (
    <div className='thermal-root px-2 py-2 text-[12px] leading-tight break-words'>
      <div className='flex flex-col items-center text-center'>
        <SellerLogo
          logoUrl={seller.logoUrl}
          alt={seller.displayName}
          size={56}
        />
        <div className='font-bold text-[15px] mt-1'>{seller.displayName}</div>
        {seller.phone && <div>{seller.phone}</div>}
        {seller.address && <div>{seller.address}</div>}
        <div className='mt-1'>------------------------------</div>
        <div className='font-bold'>FACTURE</div>
      </div>

      <div className='mt-2 space-y-1'>
        <ThermalLine label='N°' value={bill.id.slice(0, 8)} />
        <ThermalLine
          label='Date'
          value={formatDateFR(bill.incurredOn || bill.createdAt)}
        />
        <ThermalLine label='Client' value={bill.customer.name} />
        <ThermalLine label='Statut' value={billStatusFr(bill.status)} />
      </div>

      <div className='text-center mt-2'>------------------------------</div>

      <div className='mt-2 space-y-2'>
        {bill.items.map((li) => (
          <div key={`${li.name}-${li.quantity}-${li.unitPrice}`}>
            <div className='font-medium break-words'>{li.name}</div>
            <div className='flex justify-between gap-2'>
              <span className='break-words'>
                {li.quantity} x {formatCFA(li.unitPrice)}
              </span>
              <span className='tabular-nums text-right whitespace-nowrap'>
                {formatCFA(li.lineTotal)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className='text-center mt-2'>------------------------------</div>

      <div className='mt-2 space-y-1'>
        <ThermalLine label='Sous-total' value={formatCFA(bill.subtotal)} />
        <ThermalLine label='Taxes' value={formatCFA(bill.tax)} />
        <ThermalLine label='TOTAL' value={formatCFA(bill.total)} strong />
        <ThermalLine label='PAYÉ' value={formatCFA(bill.paid)} />
        <ThermalLine label='RESTANT' value={formatCFA(bill.balance)} strong />
        <ThermalLine
          label='SOLDE CLIENT'
          value={formatCFA(Math.abs(customerBalance))}
        />
      </div>

      <div className='text-center mt-2'>------------------------------</div>

      <div className='mt-3 text-center text-[11px]'>
        {seller.showOwnerIdentity ? (
          <div>{seller.poweredBy}</div>
        ) : (
          <div>Facture générée avec MOGESAPP</div>
        )}
        <div className='mt-1'>Merci pour votre achat</div>
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  strong = false,
}: {
  label: string
  value: string
  strong?: boolean
}) {
  return (
    <div
      className={cn('flex justify-between gap-3', strong && 'font-semibold')}
    >
      <span>{label}</span>
      <span className='tabular-nums whitespace-nowrap'>{value}</span>
    </div>
  )
}

function ThermalLine({
  label,
  value,
  strong = false,
}: {
  label: string
  value: string
  strong?: boolean
}) {
  return (
    <div className={cn('flex justify-between gap-3', strong && 'font-bold')}>
      <span className='break-words'>{label}</span>
      <span className='tabular-nums text-right whitespace-nowrap'>{value}</span>
    </div>
  )
}

function A4Styles() {
  return (
    <style>
      {`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
          }

          #printable-area {
            width: 100% !important;
            max-width: none !important;
            border: 0 !important;
            border-radius: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
          }
        }
      `}
    </style>
  )
}

function ThermalStyles({ mode }: { mode: 'THERMAL_58' | 'THERMAL_80' }) {
  const width = mode === 'THERMAL_58' ? '56mm' : '78mm'

  return (
    <style>
      {`
        @media print {
          @page {
            size: ${width} auto;
            margin: 0;
          }

          html, body {
            width: ${width};
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          #printable-area {
            width: ${width} !important;
            max-width: ${width} !important;
            min-width: ${width} !important;
            margin: 0 auto !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
        }
      `}
    </style>
  )
}
